import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';
import cls from 'clsx';
import styles from './input.module.css';
import { IInputProps } from './Input.types';

const BaseInput = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      className = '',
      disabled = false,
      invalid = false,
      defaultValue = '',
      value,
      onChange,
      onFocus,
      onBlur,
      prefix,
      suffix,
      ...rest
    }: IInputProps,
    ref: React.Ref<HTMLInputElement | null>
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [selfValue, setSelfValue] = useState(defaultValue);
    const isControlledComponent = useMemo(() => value !== undefined, [value]);

    useImperativeHandle(ref, () => inputRef.current);

    useEffect(() => {
      if (isControlledComponent) {
        setSelfValue(value as string);
      }
    }, [isControlledComponent, value]);

    const controlledValue = isControlledComponent
      ? { value: selfValue }
      : { defaultValue };

    const inputProps = {
      ...rest,
      ...controlledValue
    };

    const changeHandler = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelfValue(event.target.value);
        onChange?.(event);
      },
      [onChange]
    );

    const focusHandler = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
      },
      [onFocus]
    );

    const blurHandler = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(event);
      },
      [onBlur]
    );

    return (
      <div
        className={cls(
          styles.wrapper,
          {
            [styles.disabled]: disabled,
            [styles.invalid]: invalid
          },
          className
        )}
      >
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <input
          ref={inputRef}
          className={styles.input}
          disabled={disabled}
          {...inputProps}
          onChange={changeHandler}
          onFocus={focusHandler}
          onBlur={blurHandler}
        />
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>
    );
  }
);

BaseInput.displayName = 'BaseInput';

export default BaseInput;
